import { Fragment } from 'react'
import {
  Dialog as HeadlessDialog,
  Transition,
  TransitionChild
} from '@headlessui/react'
import DialogHeader from './DialogHeader'
import { cn } from '../../utils'

export interface DialogProps {
  state: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  className?: string
  closeOnClickOutside?: boolean
  closable?: boolean
  'data-testid': string
}

export default function Dialog({
  state,
  onClose,
  title,
  closeOnClickOutside,
  className,
  closable = true,
  'data-testid': testId,
  children
}: Readonly<DialogProps>) {
  return (
    <Transition show={state} as={Fragment}>
      <HeadlessDialog
        as="div"
        className="fixed inset-0 overflow-y-auto z-[100]"
        static
        onClick={(e) => e.stopPropagation()}
        onClose={() => closeOnClickOutside && onClose}
        data-testid={testId}
      >
        <div
          className={cn(
            'flex items-center justify-center min-h-screen mx-auto max-w-3xl',
            className
          )}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-black bg-opacity-80"
              data-testid={`${testId}-bg`}
            />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className="inline-block w-full p-1 my-2.5 overflow-hidden text-left transition-all transform bg-white dark:bg-cool-gray-800 dark:text-cool-gray-200 shadow-xl rounded-lg"
              data-testid={`${testId}-wrapper`}
            >
              <DialogHeader
                title={title}
                onClose={closable ? onClose : undefined}
                data-testid={`${testId}-header`}
              />
              {children}
            </div>
          </TransitionChild>
        </div>
      </HeadlessDialog>
    </Transition>
  )
}
